import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { AdminTabs } from "../components/AdminTabs";
import {
  Button,
  Card,
  ErrorNote,
  Notice,
  Spinner,
  StatusBadge,
} from "../components/primitives";
import { api } from "../lib/api";
import { MAX_MANUAL_GRANT_USD, canGrant } from "../lib/credit";
import { count, fullStamp, money, stamp } from "../lib/format";
import { LEVEL_NAMES } from "../lib/types";
import type {
  AdminUser,
  AuditEvent,
  AuditLevel,
  MemoryQuery,
} from "../lib/types";

/**
 * Approving people, and moving credit by hand.
 *
 * Registration is closed — a new sign-in lands `pending` and can do nothing
 * until someone here activates it. That is the whole reason this page exists:
 * without it the second person to sign in is stuck for ever, and the operator
 * would be running SQL against the ledger.
 *
 * The server enforces all of this; `/api/admin/*` is gated on the admin role
 * by the auth middleware, and every route here would 403 for anyone else. What
 * this page does is make it reachable.
 */
export function Admin() {
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ["admin-users"], queryFn: api.adminUsers });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    queryClient.invalidateQueries({ queryKey: ["me"] });
  };

  // Who was just let in, and whether they were told. The approval mail is
  // what replaced "they find out by trying again", so a failed one is said
  // here, where the admin can make up for it another way.
  const [letIn, setLetIn] = useState<{ email: string; notified: boolean } | null>(null);
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.adminSetStatus(id, status),
    onSuccess: (user, { status }) => {
      setLetIn(status === "active" ? { email: user.email, notified: user.notified } : null);
      refresh();
    },
  });
  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      api.adminSetRole(id, role),
    onSuccess: refresh,
  });

  if (users.isLoading) return <Spinner />;
  if (users.error) return <ErrorNote error={users.error} />;

  const pending = (users.data ?? []).filter((u) => u.status === "pending");
  const rest = (users.data ?? []).filter((u) => u.status !== "pending");
  const error = setStatus.error ?? setRole.error;

  return (
    <div className="max-w-read space-y-4">
      <AdminTabs />
      <div>
        <p className="text-sm text-ink-2">
          New sign-ins wait here until you approve them. Nobody can start an
          engagement — which spends money — until you do. Everything anyone
          does is recorded below.
        </p>
      </div>

      {error && <ErrorNote error={error} />}

      {letIn && (
        <Notice tone={letIn.notified ? "neutral" : "warning"}>
          {letIn.notified
            ? `${letIn.email} is in, and has been emailed to say so.`
            : `${letIn.email} is in, but the email telling them could not be sent. Let them know another way.`}
        </Notice>
      )}

      {pending.length > 0 && (
        <Card title={`Waiting for approval (${pending.length})`}>
          <ul className="divide-y divide-line">
            {pending.map((user) => (
              <li key={user.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <Identity user={user} />
                <div className="ml-auto flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      setStatus.mutate({ id: user.id, status: "active" })
                    }
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    onClick={() =>
                      setStatus.mutate({ id: user.id, status: "suspended" })
                    }
                  >
                    Refuse
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="People">
        <ul className="divide-y divide-line">
          {rest.map((user) => (
            <li key={user.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <Identity user={user} />
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Grant user={user} onDone={refresh} />
                <select
                  className="field field-sm w-auto"
                  value={user.role}
                  onChange={(e) =>
                    setRole.mutate({ id: user.id, role: e.target.value })
                  }
                  aria-label={`Role for ${user.email}`}
                >
                  <option value="member">member</option>
                  <option value="admin">admin</option>
                </select>
                {/* The rarest action in the row, so the quietest: it was the
                    one outlined button, louder than the grant beside it. */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    setStatus.mutate({
                      id: user.id,
                      status: user.status === "active" ? "suspended" : "active",
                    })
                  }
                >
                  {user.status === "active" ? "Suspend" : "Reinstate"}
                </Button>
              </div>
            </li>
          ))}
        </ul>
        <p className="border-t border-line px-4 py-3 text-xs leading-relaxed text-muted">
          Suspending someone takes effect on their next request — the session
          cookie carries only a user id, and the record is re-read every time.
          The last administrator cannot be demoted; there would be no route left
          to grant the role back.
        </p>
      </Card>

      <Activity />
      <GraphQueries />
    </div>
  );
}

/** How many lines of the trail a page shows. */
const PAGE = 50;

/**
 * The audit trail: who did what, and what the agent did for them.
 *
 * Deliberately not the run event stream. One run emits about a thousand
 * events and they are already durable in that run's own log; what lands here
 * is the shape of the work — started, finished, at this cost — with the run id
 * carried so the detail is one link away.
 *
 * Search is server-side and so is the total, because a page that cannot say
 * "of 4,312" leaves the reader unable to tell a narrow filter from an empty
 * table.
 */
function Activity() {
  const [q, setQ] = useState("");
  const [typed, setTyped] = useState("");
  const [kind, setKind] = useState("");
  const [level, setLevel] = useState<AuditLevel>(2);
  const [errors, setErrors] = useState(false);
  const [page, setPage] = useState(0);

  // Debounced, so typing a company name does not issue a query per keystroke
  // against a table this one is expected to grow.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQ(typed);
      setPage(0);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const events = useQuery({
    queryKey: ["admin-events", q, kind, level, errors, page],
    queryFn: () =>
      api.adminEvents({
        q,
        kind,
        level,
        errors,
        limit: PAGE,
        offset: page * PAGE,
      }),
    // Below `shape` the newest rows are a run's phases and tool calls arriving
    // as it works, so this is a live view and has to keep up. Shape changes
    // only when somebody does something.
    refetchInterval: level > 1 && !errors ? 5_000 : false,
  });

  const total = events.data?.total ?? 0;
  const shown = events.data?.events ?? [];
  const last = Math.max(0, Math.ceil(total / PAGE) - 1);

  return (
    <Card
      title="Activity"
      action={
        <span className="font-mono text-xs tabular-nums text-muted">
          {count(total)} event{total === 1 ? "" : "s"}
          {level > 1 && !errors && " · live"}
        </span>
      }
    >
      <div className="flex flex-wrap items-end gap-3 border-b border-line p-4">
        <div>
          <label className="field-label" htmlFor="audit-level">
            Show
          </label>
          <select
            id="audit-level"
            className="field"
            value={errors ? "errors" : String(level)}
            onChange={(e) => {
              const value = e.target.value;
              setErrors(value === "errors");
              if (value !== "errors") setLevel(Number(value) as AuditLevel);
              setPage(0);
            }}
          >
            <option value="1">1 · Shape — what happened</option>
            <option value="2">2 · Detail — and how</option>
            <option value="3">3 · Trace — every tool call</option>
            <option value="errors">Errors only</option>
          </select>
        </div>
        <div className="min-w-56 flex-1">
          <label className="field-label" htmlFor="audit-q">
            Search
          </label>
          <input
            id="audit-q"
            className="field"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="A person, a company, a run id"
          />
        </div>
        <div>
          <label className="field-label" htmlFor="audit-kind">
            Kind
          </label>
          <select
            id="audit-kind"
            className="field"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setPage(0);
            }}
          >
            <option value="">Everything</option>
            {(events.data?.kinds ?? []).map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>
      </div>

      {events.isLoading && <Spinner />}
      {events.error && <ErrorNote error={events.error} />}

      {!events.isLoading &&
        !errors &&
        events.data &&
        level > events.data.writing && (
          <p className="border-b border-line bg-surface-2 px-4 py-3 text-sm text-muted">
            This deployment records to{" "}
            <span className="font-medium text-primary">
              {LEVEL_NAMES[events.data.writing]}
            </span>
            . Nothing deeper is written, so this view is empty by
            configuration rather than because nothing happened — set{" "}
            <code className="font-mono text-xs">CA_AUDIT_LEVEL=3</code> to
            record every tool call.
          </p>
        )}

      {events.isLoading && <Spinner />}
      {events.error && <ErrorNote error={events.error} />}

      {!events.isLoading && shown.length === 0 && (
        <p className="px-4 py-6 text-sm text-muted">
          {errors && !q && !kind
            ? "Nothing has gone wrong. Failed sign-ins, refusals and runs that did not finish appear here."
            : q || kind
              ? "Nothing matches that."
              : "Nothing recorded yet. Sign-ins, approvals, uploads and runs appear here."}
        </p>
      )}

      <ul className="divide-y divide-line">
        {shown.map((event) => (
          <Line key={event.id} event={event} />
        ))}
      </ul>

      {total > PAGE && (
        <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2 text-xs">
          <Button
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Newer
          </Button>
          <span className="font-mono text-xs tabular-nums text-muted">
            {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of{" "}
            {count(total)}
          </span>
          <Button
            size="sm"
            disabled={page >= last}
            onClick={() => setPage((p) => Math.min(last, p + 1))}
          >
            Older
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * What the agent asked the knowledge graph, and what came back.
 *
 * A second facility beside the trail rather than a filter on it. The trail is
 * sentences about what people did; this is Cypher, its parameters and a slice
 * of a result — three fields nothing else has, at a volume nothing else
 * reaches.
 *
 * The question it exists for: a retrieval that found nothing and a graph that
 * holds nothing are indistinguishable from outside the process. Reading the
 * statement and the rows beside each other is the only way to tell them apart.
 */
function GraphQueries() {
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [mode, setMode] = useState<"" | "read" | "write">("read");
  const [errors, setErrors] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQ(typed);
      setPage(0);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const queries = useQuery({
    queryKey: ["admin-memory-queries", q, mode, errors, page],
    queryFn: () =>
      api.adminMemoryQueries({
        q,
        mode: mode || undefined,
        errors,
        limit: PAGE,
        offset: page * PAGE,
      }),
    // A running engagement queries the graph continuously; this is the panel
    // somebody watches while it does.
    refetchInterval: 5_000,
  });

  const total = queries.data?.total ?? 0;
  const shown = queries.data?.queries ?? [];
  const last = Math.max(0, Math.ceil(total / PAGE) - 1);

  return (
    <Card
      title="Knowledge graph"
      action={
        <span className="font-mono text-xs tabular-nums text-muted">
          {count(total)} quer{total === 1 ? "y" : "ies"} · live
        </span>
      }
    >
      <div className="flex flex-wrap items-end gap-3 border-b border-line p-4">
        <div>
          <label className="field-label" htmlFor="mem-mode">
            Show
          </label>
          <select
            id="mem-mode"
            className="field"
            value={errors ? "errors" : mode}
            onChange={(e) => {
              const value = e.target.value;
              setErrors(value === "errors");
              if (value !== "errors") setMode(value as "" | "read" | "write");
              setPage(0);
            }}
          >
            <option value="read">Reads — what it looked for</option>
            <option value="write">Writes — what it stored</option>
            <option value="">Everything</option>
            <option value="errors">Failed queries</option>
          </select>
        </div>
        <div className="min-w-56 flex-1">
          <label className="field-label" htmlFor="mem-q">
            Search
          </label>
          <input
            id="mem-q"
            className="field"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="A search term, a label, a run id"
          />
        </div>
      </div>

      {queries.isLoading && <Spinner />}
      {queries.error && <ErrorNote error={queries.error} />}

      {!queries.isLoading && shown.length === 0 && (
        <p className="px-4 py-6 text-sm text-muted">
          {q || errors
            ? "Nothing matches that."
            : "Nothing yet. Every statement a run puts to the graph appears here, with what came back."}
        </p>
      )}

      <ul className="divide-y divide-line">
        {shown.map((query) => (
          <QueryLine key={query.id} query={query} />
        ))}
      </ul>

      {total > PAGE && (
        <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2 text-xs">
          <Button
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Newer
          </Button>
          <span className="font-mono text-xs tabular-nums text-muted">
            {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of{" "}
            {count(total)}
          </span>
          <Button
            size="sm"
            disabled={page >= last}
            onClick={() => setPage((p) => Math.min(last, p + 1))}
          >
            Older
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * One statement, closed. Open it for the Cypher and the rows.
 *
 * The row line carries what identifies a query at a glance — what it was
 * looking for, and how many rows it got — because "which of these forty
 * searches came back empty" is the question somebody scans this page with.
 */
function QueryLine({ query }: { query: MemoryQuery }) {
  const [open, setOpen] = useState(false);
  const when = query.at ? new Date(query.at) : null;
  const asked = query.params
    ? Object.values(query.params).find((v) => typeof v === "string" && v.length > 2)
    : null;

  return (
    <li className="px-4 py-2.5 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <time
          className="w-36 shrink-0 font-mono text-xs tabular-nums text-muted"
          dateTime={query.at ?? undefined}
          title={when ? fullStamp(when) : undefined}
        >
          {when && stamp(when, true)}
        </time>
        <span className="shrink-0 font-mono text-xs text-muted">{query.operation ?? query.mode}</span>
        <span className="min-w-0 flex-1 truncate text-ink">
          {typeof asked === "string" ? asked : query.cypher}
        </span>
        {query.error ? (
          <StatusBadge tone="critical">failed</StatusBadge>
        ) : (
          <span
            //     Nothing matched is worth a word, not the finding's colour:
            //     eleven of these in orange made the log the loudest page.
            className={`shrink-0 font-mono text-xs tabular-nums ${
              query.rows === 0 ? "text-ink" : "text-muted"
            }`}
            title={query.rows === 0 ? "This query matched nothing" : undefined}
          >
            {query.rows} row{query.rows === 1 ? "" : "s"}
          </span>
        )}
        <span className="shrink-0 font-mono text-xs tabular-nums text-muted">
          {query.duration_ms}ms
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="shrink-0 text-xs text-muted underline"
        >
          {open ? "less" : "cypher"}
        </button>
      </div>
      <div className="mt-0.5 flex flex-wrap gap-x-3 pl-[9.75rem] text-xs text-muted">
        <span>{query.actor ?? "system"}</span>
        {query.run_id && (
          <Link
            to={`/engagements/${encodeURIComponent(query.run_id)}`}
            className="text-accent"
          >
            {query.run_id}
          </Link>
        )}
      </div>
      {open && (
        <div className="mt-2 space-y-2">
          <pre className="overflow-x-auto rounded-sm border border-line bg-surface-2 p-3 text-xs text-ink-2">
            {query.cypher}
          </pre>
          {query.params && (
            <pre className="overflow-x-auto rounded-sm border border-line bg-surface-2 p-3 text-xs text-ink-2">
              {JSON.stringify(query.params, null, 2)}
            </pre>
          )}
          {query.error ? (
            <pre className="overflow-x-auto rounded-sm border border-line bg-surface-2 p-3 text-xs text-critical-text">
              {query.error}
            </pre>
          ) : (
            <pre className="max-h-72 overflow-auto rounded-sm border border-line bg-surface-2 p-3 text-xs text-ink-2">
              {query.result === null || query.result === undefined
                ? "No rows."
                : JSON.stringify(query.result, null, 2)}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}

function Line({ event }: { event: AuditEvent }) {
  const [open, setOpen] = useState(false);
  const when = new Date(event.at);
  return (
    <li className="px-4 py-2.5 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <time
          className="w-36 shrink-0 font-mono text-xs tabular-nums text-muted"
          dateTime={event.at}
          title={fullStamp(when)}
        >
          {stamp(when)}
        </time>
        <span className="shrink-0 font-mono text-xs text-muted">{event.kind}</span>
        <span className="min-w-0 flex-1 text-ink">{event.summary}</span>
        {event.run_id && (
          <Link
            to={`/engagements/${encodeURIComponent(event.run_id)}`}
            className="shrink-0 text-xs text-accent"
          >
            open run
          </Link>
        )}
        {event.detail && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="shrink-0 text-xs text-muted underline"
          >
            {open ? "less" : "detail"}
          </button>
        )}
      </div>
      <div className="mt-0.5 pl-[9.75rem] text-xs text-muted">
        {event.actor ?? "system"}
      </div>
      {open && event.detail && (
        <pre className="mt-2 overflow-x-auto rounded-sm border border-line bg-surface-2 p-3 text-xs text-ink-2">
          {JSON.stringify(event.detail, null, 2)}
        </pre>
      )}
    </li>
  );
}

function Identity({ user }: { user: AdminUser }) {
  return (
    <div className="min-w-48">
      <div className="flex items-center gap-2 text-sm text-ink">
        {user.name ?? user.email}
        {user.role === "admin" && (
          <StatusBadge tone="neutral">admin</StatusBadge>
        )}
        {user.status === "suspended" && (
          <StatusBadge tone="critical">suspended</StatusBadge>
        )}
      </div>
      <div className="font-mono text-xs text-muted">
        {user.email} · {money(user.available_usd)} available
        {user.held_usd > 0 && ` · ${money(user.held_usd)} reserved`}
      </div>
      {user.notify_email && (
        <div className="font-mono text-xs text-muted">notices to {user.notify_email}</div>
      )}
    </div>
  );
}

/**
 * Signed, so one control covers a grant and a correction.
 *
 * It writes an entry to the same append-only ledger as everything else — never
 * an edit to a balance, because a balance you can write is one that can
 * disagree with its history.
 */
function Grant({ user, onDone }: { user: AdminUser; onDone: () => void }) {
  const [amount, setAmount] = useState("");
  const grant = useMutation({
    mutationFn: () => api.adminGrant(user.id, Number(amount), "Granted by an admin"),
    onSuccess: () => {
      setAmount("");
      onDone();
    },
  });

  const value = Number(amount);
  const entered = amount.trim() !== "" && Number.isFinite(value);
  const usable = entered && canGrant(value);
  // Said before the request, not after: the server refuses this too, but an
  // admin who has typed an extra zero should see it while the field still
  // holds what they typed.
  const tooLarge = entered && value > MAX_MANUAL_GRANT_USD;

  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (usable) grant.mutate();
      }}
    >
      <input
        className="field field-sm w-24"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder="$ ±"
        inputMode="decimal"
        aria-label={`Credit to grant ${user.email} (max $${MAX_MANUAL_GRANT_USD})`}
      />
      <Button
        type="submit"
        size="sm"
        disabled={!usable || grant.isPending}
      >
        {grant.isPending ? "…" : "Grant"}
      </Button>
      {tooLarge ? (
        <span className="text-xs text-ink-2">max ${MAX_MANUAL_GRANT_USD}</span>
      ) : null}
    </form>
  );
}
